import Cookies from "js-cookie";
import thestore from "@/applibs/thestore.js";

const IDLE_MINUTES = Number(process.env.VUE_APP_IDLE_TIMEOUT_MINUTES || 5);
const IDLE_MS = (IDLE_MINUTES > 0 ? IDLE_MINUTES : 5) * 60 * 1000;
const CHECK_EVERY_MS = 1000;
const ACTIVITY_THROTTLE_MS = 1000;
const LAST_ACTIVITY_KEY = "pgx_last_activity";
const LOGOUT_BROADCAST_KEY = "pgx_idle_logout_event";
const HOME_REDIRECT_KEY = "pgx_session_timeout_redirect_home";
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];

let checkTimer = null;
let started = false;
let timeoutInProgress = false;
let activityThrottled = false;
let onTimeoutCallback = null;

function isLoggedIn() {
  return Boolean(Cookies.get("usid"));
}

function setLastActivity(at) {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(at || Date.now()));
  } catch (_) {
    /* ignore quota / private mode */
  }
}

function getLastActivity() {
  try {
    return Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
  } catch (_) {
    return 0;
  }
}

function markActivity() {
  if (!started || timeoutInProgress || !isLoggedIn() || activityThrottled) {
    return;
  }
  activityThrottled = true;
  setLastActivity(Date.now());
  window.setTimeout(function () {
    activityThrottled = false;
  }, ACTIVITY_THROTTLE_MS);
}

function onVisibilityChange() {
  if (document.visibilityState === "visible") {
    checkIdle();
  }
}

function onStorage(event) {
  if (event.key === LOGOUT_BROADCAST_KEY && event.newValue) {
    finishLocalLogout();
  }
}

function addListeners() {
  ACTIVITY_EVENTS.forEach(function (name) {
    window.addEventListener(name, markActivity, true);
  });
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("storage", onStorage);
}

function removeListeners() {
  ACTIVITY_EVENTS.forEach(function (name) {
    window.removeEventListener(name, markActivity, true);
  });
  document.removeEventListener("visibilitychange", onVisibilityChange);
  window.removeEventListener("storage", onStorage);
}

function stop() {
  started = false;
  if (checkTimer) {
    window.clearInterval(checkTimer);
    checkTimer = null;
  }
  removeListeners();
}

function finishLocalLogout() {
  if (timeoutInProgress) {
    return;
  }
  timeoutInProgress = true;
  stop();
  try {
    sessionStorage.setItem(HOME_REDIRECT_KEY, "1");
  } catch (_) {
    /* ignore */
  }
  Cookies.remove("jwt");
  Cookies.remove("usid");
  if (typeof onTimeoutCallback === "function") {
    onTimeoutCallback();
  }
}

async function handleTimeout() {
  if (timeoutInProgress) {
    return;
  }

  var token = Cookies.get("jwt") || Cookies.get("usid");
  finishLocalLogout();

  try {
    localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
  } catch (_) {
    /* ignore */
  }

  try {
    await thestore.postAuthLogout(token);
  } catch (_) {
    /* already logged out locally */
  }
}

function checkIdle() {
  if (!started || timeoutInProgress) {
    return;
  }
  if (!isLoggedIn()) {
    stop();
    return;
  }

  var last = getLastActivity();
  if (!last) {
    setLastActivity(Date.now());
    return;
  }
  if (Date.now() - last >= IDLE_MS) {
    handleTimeout();
  }
}

function start(callback) {
  onTimeoutCallback = callback;
  if (started || timeoutInProgress || !isLoggedIn()) {
    return;
  }

  started = true;
  setLastActivity(Date.now());
  addListeners();
  checkTimer = window.setInterval(checkIdle, CHECK_EVERY_MS);
}

function sync(callback) {
  onTimeoutCallback = callback;
  if (!isLoggedIn()) {
    if (!timeoutInProgress) {
      stop();
    }
    return;
  }
  if (!started) {
    timeoutInProgress = false;
    start(callback);
  }
}

let redirectHomeOnBoot = false;
try {
  if (sessionStorage.getItem(HOME_REDIRECT_KEY) === "1") {
    sessionStorage.removeItem(HOME_REDIRECT_KEY);
    redirectHomeOnBoot = true;
  }
} catch (_) {
  redirectHomeOnBoot = false;
}

function shouldRedirectHomeOnBoot() {
  return redirectHomeOnBoot;
}

function clearHomeRedirectFlag() {
  redirectHomeOnBoot = false;
  try {
    sessionStorage.removeItem(HOME_REDIRECT_KEY);
  } catch (_) {
    /* ignore */
  }
}

export default {
  IDLE_MS,
  start,
  stop,
  sync,
  isLoggedIn,
  shouldRedirectHomeOnBoot,
  clearHomeRedirectFlag,
};
